/**
 * Profile Questionnaire role presentation mapping.
 *
 * This module is the **single source of truth** for converting an
 * internal scenario + gender pair into the canonical user-facing
 * labels (questionnaire wording, search/display wording).
 *
 * Authority:
 * - docs/epics/profile-discovery-chat-br-reconciliation01/partner-corrections.md
 *   §1 (PC-001..PC-007)
 * - docs/epics/profile-discovery-chat-br-reconciliation01/canonical-product-contract.md
 *   §1, §2
 * - docs/epics/profile-questionnaire-detail-slice/crosswalk.md
 *
 * Elm-ish doctrine: this module is pure. No API calls, no hooks, no
 * I/O. Its exhaustive table tests live in
 * __tests__/rolePresentation.test.ts.
 *
 * Do NOT scatter the strings "Спонсор" / "Состоятельный" /
 * "Состоятельная" / "Равный партнёр" / "Милый друг" / "Подружка" /
 * "Я не хочу работать" across components. Route them through here.
 */

export type InternalScenario = 'equal' | 'sponsor' | 'no_work';

export type InternalGender = 'male' | 'female';

export interface ScenarioPresentation {
  /** Questionnaire wording shown inside the form. */
  readonly questionnaireLabel: string;
  /** Search/Discovery label visible to other users, keyed by viewer gender. */
  readonly discoveryLabel: (gender: InternalGender) => string;
}

/**
 * The canonical mapping. Three entries only — there are exactly three
 * product scenarios per CONTRACT-001.1.
 */
export const SCENARIO_PRESENTATION: Readonly<Record<InternalScenario, ScenarioPresentation>> =
  Object.freeze({
    equal: {
      questionnaireLabel: 'Я богат/богата, ищу такого же',
      discoveryLabel: () => 'Равный партнёр',
    },
    sponsor: {
      questionnaireLabel: 'Спонсор',
      discoveryLabel: (gender) =>
        gender === 'female' ? 'Состоятельная' : 'Состоятельный',
    },
    no_work: {
      questionnaireLabel: 'Я не хочу работать',
      discoveryLabel: (gender) =>
        gender === 'female' ? 'Подружка' : 'Милый друг',
    },
  });

/** All scenario labels in display order. */
export const SCENARIO_ORDER: readonly InternalScenario[] =
  Object.freeze(['equal', 'sponsor', 'no_work']);

/**
 * Map a backend `Goal` enum value to its product scenario. The mapping
 * is stable; `companion_seeker` and `no_work` both belong to the
 * scenario-C product concept (per crosswalk.md §4) but retain distinct
 * internal enum strings so stored data stays truthful.
 */
export function goalToScenario(
  goal: 'equal_seeker' | 'companion_seeker' | 'no_work' | 'wealthy_seeker'
): InternalScenario {
  switch (goal) {
    case 'equal_seeker':
      return 'equal';
    case 'wealthy_seeker':
      return 'sponsor';
    case 'companion_seeker':
    case 'no_work':
      return 'no_work';
  }
}

/**
 * Map an internal scenario + gender pair to its discovery label.
 * Pure; defensive; exhaustive over `InternalScenario`.
 */
export function discoveryLabelFor(
  scenario: InternalScenario,
  gender: InternalGender
): string {
  return SCENARIO_PRESENTATION[scenario].discoveryLabel(gender);
}

/**
 * Map an internal scenario to its questionnaire label.
 */
export function questionnaireLabelFor(scenario: InternalScenario): string {
  return SCENARIO_PRESENTATION[scenario].questionnaireLabel;
}